import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { BubbleNode, ChainId, Cluster, MapData } from '@pleco/shared';
import {
  CHAINS,
  formatAge,
  formatCompact,
  formatPercent,
  formatUsd,
  gradeColor,
  riskColor,
  shortAddress,
} from '@pleco/shared';
import { api } from '../lib/api.js';

/* ------------------------------------------------------------------ *
 * Token header
 * ------------------------------------------------------------------ */

export function TokenHeader({ data, onWatch }: { data: MapData; onWatch: () => void }) {
  const t = data.token;
  const chain = CHAINS[t.chain as ChainId];
  return (
    <div className="panel">
      <div style={{ display: 'flex', gap: 11, alignItems: 'center' }}>
        {t.logoUrl ? (
          <img className="token-logo" style={{ width: 38, height: 38, flex: '0 0 38px' }} src={t.logoUrl} alt="" />
        ) : (
          <div className="token-logo" style={{ width: 38, height: 38, flex: '0 0 38px' }}>
            {t.symbol.slice(0, 3)}
          </div>
        )}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <strong style={{ fontSize: 17, letterSpacing: '-0.02em' }}>{t.symbol}</strong>
            <span className="chip" style={{ color: chain?.color }}>
              {chain?.label ?? t.chain}
            </span>
          </div>
          <div className="muted" style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {t.name}
          </div>
        </div>
        <button className="btn sm" onClick={onWatch} title="Watch & get alerts">
          ☆
        </button>
        <Link
          className="btn sm"
          to={`/compare?t=${encodeURIComponent(`${t.chain}:${t.address}`)}`}
          title="Compare with other tokens"
        >
          ⇆
        </Link>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 12px', marginTop: 12 }}>
        <Kv label="Price" value={t.priceUsd ? formatUsd(t.priceUsd) : '—'} />
        <Kv
          label="24h"
          value={t.priceChange24h !== undefined ? `${t.priceChange24h > 0 ? '+' : ''}${t.priceChange24h.toFixed(1)}%` : '—'}
          color={t.priceChange24h === undefined ? undefined : t.priceChange24h >= 0 ? 'var(--accent)' : 'var(--danger)'}
        />
        <Kv label="Mcap" value={t.marketCapUsd ? formatUsd(t.marketCapUsd) : '—'} />
        <Kv label="Liquidity" value={t.liquidityUsd ? formatUsd(t.liquidityUsd) : '—'} />
        <Kv label="Supply" value={formatCompact(t.trackedSupply)} />
        <Kv label="Holders" value={formatCompact(data.metrics.holders)} />
      </div>

      <div className="addr" style={{ marginTop: 10 }}>
        {shortAddress(t.address, 10, 8)}
        <button
          onClick={() => {
            void navigator.clipboard.writeText(t.address);
          }}
          title="Copy address"
        >
          copy
        </button>
        {chain && (
          <a href={chain.explorer.token(t.address)} target="_blank" rel="noreferrer" style={{ color: 'var(--text-faint)', fontSize: 11 }}>
            explorer ↗
          </a>
        )}
      </div>
    </div>
  );
}

function Kv({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div className="kv">
      <dt>{label}</dt>
      <dd style={color ? { color } : undefined}>{value}</dd>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Decentralization score
 * ------------------------------------------------------------------ */

export function ScorePanel({ data }: { data: MapData }) {
  const d = data.metrics.decentralization;
  const color = gradeColor(d.grade);
  const circumference = 2 * Math.PI * 38;
  const dash = (d.score / 100) * circumference;

  return (
    <div className="panel">
      <div className="panel-title">
        Decentralization score
        <span className="faint" style={{ textTransform: 'none', letterSpacing: 0 }}>
          0–100
        </span>
      </div>
      <div className="score-wrap">
        <div className="gauge">
          <svg width="92" height="92">
            <circle cx="46" cy="46" r="38" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="8" />
            <circle
              cx="46"
              cy="46"
              r="38"
              fill="none"
              stroke={color}
              strokeWidth="8"
              strokeLinecap="round"
              strokeDasharray={`${dash} ${circumference}`}
              style={{ transition: 'stroke-dasharray 0.5s ease' }}
            />
          </svg>
          <div className="gauge-value" style={{ color: d.notAssessable ? 'var(--text-faint)' : color }}>
            {d.notAssessable ? '—' : d.score.toFixed(0)}
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="grade-label" style={{ color: d.notAssessable ? 'var(--text-dim)' : color }}>
            {d.notAssessable ? 'not enough holders' : d.grade}
          </div>
          <div className="muted" style={{ fontSize: 12, marginTop: 6, lineHeight: 1.5 }}>
            {d.explanation[0]}
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 9, fontSize: 11 }} className="faint mono">
            <span>HHI {d.hhi.toFixed(3)}</span>
            <span>Gini {d.gini.toFixed(2)}</span>
            <span>N{'\u2091'} {d.effectiveEntities.toFixed(1)}</span>
          </div>
        </div>
      </div>
      <details style={{ marginTop: 10 }}>
        <summary className="faint" style={{ fontSize: 11, cursor: 'pointer' }}>
          How this was computed
        </summary>
        <ul className="muted" style={{ fontSize: 12, paddingLeft: 16, margin: '8px 0 0', lineHeight: 1.6 }}>
          {d.explanation.map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      </details>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Supply breakdown
 * ------------------------------------------------------------------ */

const SUPPLY_SEGMENTS: { key: keyof MapData['metrics']['supply']; label: string; color: string }[] = [
  { key: 'clustered', label: 'Clustered', color: '#ff7a45' },
  { key: 'cex', label: 'CEX', color: '#8ea2c6' },
  { key: 'dex', label: 'DEX / LP', color: '#4ad7d1' },
  { key: 'contracts', label: 'Contracts', color: '#5c6882' },
  { key: 'burned', label: 'Burned', color: '#45506a' },
  { key: 'freshWallets', label: 'Fresh wallets', color: '#ffd166' },
  { key: 'bundles', label: 'Bundled', color: '#ff4d6d' },
  { key: 'snipers', label: 'Snipers', color: '#b388ff' },
];

export function SupplyPanel({ data }: { data: MapData }) {
  const s = data.metrics.supply;
  const segments = SUPPLY_SEGMENTS.map((seg) => ({ ...seg, value: Number(s[seg.key] ?? 0) })).filter(
    (seg) => seg.value > 0.01,
  );
  const shown = segments.slice(0, 5);
  const total = Math.min(100, shown.reduce((a, b) => a + b.value, 0));

  return (
    <div className="panel">
      <div className="panel-title">Supply breakdown</div>
      <div className="bar">
        {shown.map((seg) => (
          <i
            key={seg.key}
            className="bar-seg"
            style={{ width: `${seg.value}%`, background: seg.color }}
            title={`${seg.label}: ${formatPercent(seg.value)}`}
          />
        ))}
        <i className="bar-seg" style={{ width: `${Math.max(0, 100 - total)}%`, background: 'rgba(255,255,255,0.06)' }} />
      </div>
      <div className="stack-legend">
        {segments.map((seg) => (
          <span key={seg.key}>
            <i style={{ background: seg.color }} />
            {seg.label} <b className="mono">{formatPercent(seg.value, 1)}</b>
          </span>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px', marginTop: 10 }}>
        <Kv label="Top 10" value={formatPercent(s.top10, 1)} />
        <Kv label="Top 10 (organic)" value={formatPercent(s.top10Adjusted, 1)} />
        <Kv label="Top 50" value={formatPercent(s.top50, 1)} />
        <Kv label="Organic" value={formatPercent(s.organic, 1)} />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Risk
 * ------------------------------------------------------------------ */

export function RiskPanel({ data }: { data: MapData }) {
  const r = data.metrics.risk;
  const color = riskColor(r.level);
  return (
    <div className="panel">
      <div className="panel-title">
        Risk report
        {r.notAssessable ? (
          <span className="chip">n/a</span>
        ) : (
          <span className="chip" style={{ color, borderColor: color }}>
            {r.level} · {r.score}
          </span>
        )}
      </div>
      <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
        {r.summary}
      </div>
      {r.flags.length === 0 && !r.notAssessable && <div className="empty-state">Nothing suspicious detected.</div>}
      {r.flags.map((f) => (
        <div className="flag" key={f.id}>
          <i className="flag-dot" style={{ background: riskColor(f.level) }} />
          <div style={{ minWidth: 0 }}>
            <div className="flag-title">{f.title}</div>
            <div className="flag-detail">{f.detail}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Clusters
 * ------------------------------------------------------------------ */

export function ClusterList({
  data,
  selectedId,
  onSelect,
}: {
  data: MapData;
  selectedId?: number;
  onSelect: (c?: Cluster) => void;
}) {
  if (data.clusters.length === 0) {
    return (
      <div className="panel">
        <div className="panel-title">Clusters</div>
        <div className="empty-state">
          No connected wallet groups found.
          <br />
          Holders appear to have acquired independently.
        </div>
      </div>
    );
  }

  return (
    <div className="panel" style={{ padding: '14px 0' }}>
      <div className="panel-title" style={{ padding: '0 16px' }}>
        Clusters
        <span className="faint" style={{ letterSpacing: 0, textTransform: 'none' }}>
          {data.clusters.length} groups
        </span>
      </div>
      {data.clusters.slice(0, 14).map((c) => (
        <div
          key={c.id}
          className={`row-item ${selectedId === c.id ? 'sel' : ''}`}
          onClick={() => onSelect(selectedId === c.id ? undefined : c)}
        >
          <i className="row-swatch" style={{ background: c.color }} />
          <div className="row-main">
            <div className="row-title">
              Cluster #{c.rank}
              <span className="chip">{c.kind}</span>
            </div>
            <div className="row-sub">
              {c.members.length} wallets · density {(c.density * 100).toFixed(0)}%
            </div>
          </div>
          <div className="row-right" style={{ color: c.color }}>
            {formatPercent(c.percent)}
            {c.valueUsd ? <div className="faint" style={{ fontSize: 10 }}>{formatUsd(c.valueUsd)}</div> : null}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Holder list
 * ------------------------------------------------------------------ */

export function HolderList({
  data,
  selectedId,
  onSelect,
}: {
  data: MapData;
  selectedId?: string;
  onSelect: (n: BubbleNode) => void;
}) {
  const holders = [...data.nodes].sort((a, b) => b.percent - a.percent).slice(0, 60);
  return (
    <div className="panel" style={{ padding: '14px 0' }}>
      <div className="panel-title" style={{ padding: '0 16px' }}>
        Top holders
      </div>
      {holders.map((n) => (
        <div
          key={n.id}
          className={`row-item ${selectedId === n.id ? 'sel' : ''}`}
          onClick={() => onSelect(n)}
        >
          <i
            className="row-swatch"
            style={{ background: n.clusterId ? data.clusters.find((c) => c.id === n.clusterId)?.color : '#3fa9ff' }}
          />
          <div className="row-main">
            <div className="row-title">
              {n.label ?? shortAddress(n.address, 7, 5)}
              {n.tags.includes('bundler') && <span className="chip danger">bundle</span>}
              {n.tags.includes('sniper') && <span className="chip warn">sniper</span>}
              {n.origin === 'magic' && <span className="chip">funding wallet</span>}
            </div>
            <div className="row-sub">
              {n.kind}
              {n.firstHeld ? ` · held ${formatAge(n.firstHeld)}` : ''}
            </div>
          </div>
          <div className="row-right">{formatPercent(n.percent)}</div>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Node inspector
 * ------------------------------------------------------------------ */

export function NodeInspector({ node, data, onClose }: { node: BubbleNode; data: MapData; onClose: () => void }) {
  const [transfers, setTransfers] = useState<
    { hash: string; from: string; to: string; amount: number; timestamp: number; direction: 'in' | 'out' }[]
  >([]);
  const [loading, setLoading] = useState(false);
  const chain = CHAINS[node.chain as ChainId];
  const cluster = data.clusters.find((c) => c.id === node.clusterId);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .transfers(node.chain, data.token.address, node.address)
      .then(({ transfers }) => !cancelled && setTransfers(transfers.slice(0, 25)))
      .catch(() => undefined)
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [data.token.address, node.address, node.chain]);

  const neighbours = data.edges
    .filter((e) => e.source === node.id || e.target === node.id)
    .map((e) => ({ edge: e, other: e.source === node.id ? e.target : e.source }))
    .slice(0, 12);

  return (
    <div className="panel">
      <div className="panel-title">
        Wallet
        <button className="btn ghost sm" onClick={onClose}>
          ✕
        </button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <i
          className="row-swatch"
          style={{ width: 14, height: 14, background: cluster?.color ?? '#3fa9ff' }}
        />
        <strong>{node.label ?? (node.rank > 0 ? `Holder #${node.rank}` : 'Linked wallet')}</strong>
        {node.entity && <span className="chip accent">{node.entity}</span>}
      </div>

      <div className="addr" style={{ marginBottom: 10 }}>
        {shortAddress(node.address, 12, 10)}
        <button onClick={() => void navigator.clipboard.writeText(node.address)}>copy</button>
        {chain && (
          <a href={chain.explorer.address(node.address)} target="_blank" rel="noreferrer" style={{ fontSize: 11 }}>
            ↗
          </a>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
        <Kv label="Supply" value={formatPercent(node.percent)} />
        <Kv label="Value" value={node.valueUsd ? formatUsd(node.valueUsd) : '—'} />
        <Kv label="Balance" value={formatCompact(node.amount)} />
        <Kv label="Type" value={node.kind} />
        <Kv label="Links" value={String(node.degree ?? 0)} />
        <Kv label="First held" value={node.firstHeld ? formatAge(node.firstHeld) : '—'} />
      </div>

      {node.tags.length > 0 && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 10 }}>
          {node.tags.map((t) => (
            <span key={t} className={`chip ${t === 'bundler' ? 'danger' : t === 'sniper' ? 'warn' : ''}`}>
              {t.replace('_', ' ')}
            </span>
          ))}
        </div>
      )}

      {cluster && (
        <div
          style={{
            marginTop: 12,
            padding: 10,
            borderRadius: 'var(--r-sm)',
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid var(--border)',
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 650, color: cluster.color }}>
            Member of cluster #{cluster.rank}
          </div>
          <div className="muted" style={{ fontSize: 12, marginTop: 3 }}>
            {cluster.members.length} wallets holding {formatPercent(cluster.percent)} of supply
            {cluster.rootAddress ? `, seeded by ${shortAddress(cluster.rootAddress)}` : ''}.
          </div>
        </div>
      )}

      {neighbours.length > 0 && (
        <>
          <div className="panel-title" style={{ marginTop: 16 }}>
            Connections
          </div>
          {neighbours.map(({ edge, other }) => {
            const o = data.nodes.find((n) => n.id === other);
            return (
              <div className="transfer-row" key={edge.id}>
                <span className="chip" style={{ fontSize: 10 }}>
                  {edge.kind}
                </span>
                <span className="mono" style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {o?.label ?? shortAddress(o?.address ?? other, 6, 4)}
                </span>
                <span className="faint mono">{formatCompact(edge.amount)}</span>
              </div>
            );
          })}
        </>
      )}

      <div className="panel-title" style={{ marginTop: 16 }}>
        Recent transfers
        {loading && <span className="spinner" />}
      </div>
      {transfers.length === 0 && !loading && <div className="empty-state">No indexed transfers.</div>}
      {transfers.map((t) => (
        <div className="transfer-row" key={`${t.hash}-${t.timestamp}-${t.direction}`}>
          <span className={t.direction === 'in' ? 'dir-in' : 'dir-out'}>{t.direction === 'in' ? '↓' : '↑'}</span>
          <span className="mono" style={{ flex: 1 }}>
            {shortAddress(t.direction === 'in' ? t.from : t.to, 6, 4)}
          </span>
          <span className="mono">{formatCompact(t.amount)}</span>
          <span className="faint mono" style={{ fontSize: 10 }}>
            {formatAge(t.timestamp)}
          </span>
        </div>
      ))}
    </div>
  );
}
